'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import LuxuryModal from '@/components/ui/LuxuryModal';

interface SavedPackage {
  id: string;
  package_reference: string;
  name: string;
  package_type: string;
  status: string;
  components: Array<{ type: string; supplier: string; product_name: string; estimated_price_usd: number }>;
  pricing: Record<string, unknown>;
  itinerary: Record<string, unknown>;
  metadata: Record<string, unknown>;
  created_at: string;
}

const PACKAGE_TYPE_ICONS: Record<string, string> = {
  luxury: 'StarIcon',
  family: 'UsersIcon',
  honeymoon: 'HeartIcon',
  business: 'BriefcaseIcon',
  adventure: 'BoltIcon',
  cultural: 'GlobeAltIcon',
  wellness: 'SparklesIcon',
  custom: 'PuzzlePieceIcon',
};

const COMPONENT_ICONS: Record<string, string> = {
  flight: 'PaperAirplaneIcon',
  hotel: 'BuildingOffice2Icon',
  activity: 'StarIcon',
  tour: 'MapIcon',
  cruise: 'GlobeAltIcon',
  transfer: 'TruckIcon',
  bus: 'TruckIcon',
  insurance: 'ShieldCheckIcon',
};

export default function CustomerTripBuilderContent() {
  const [savedPackages, setSavedPackages] = useState<SavedPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<SavedPackage | null>(null);
  const [activeModal, setActiveModal] = useState<'detail' | 'compare' | 'share' | null>(null);
  const [compareList, setCompareList] = useState<string[]>([]);
  const [shareLink, setShareLink] = useState('');
  const [filter, setFilter] = useState<'all' | 'draft' | 'confirmed' | 'booked'>('all');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/dynamic-packages');
      const data = await res.json();
      setSavedPackages(data.packages || []);
    } catch { /* silent */ } finally {
      setIsLoading(false);
    }
  };

  const handleShare = (pkg: SavedPackage) => {
    const link = `${window.location.origin}/dynamic-packages?ref=${pkg.package_reference}`;
    setShareLink(link);
    setSelectedPackage(pkg);
    setActiveModal('share');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      // copied
    });
  };

  const toggleCompare = (id: string) => {
    setCompareList((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const filteredPackages = savedPackages.filter((p) => {
    if (filter === 'all') return true;
    return p.status === filter;
  });

  const getTotal = (pkg: SavedPackage): number => {
    const pricing = pkg.pricing as Record<string, unknown>;
    const summary = pricing?.pricing_summary as Record<string, unknown>;
    return (summary?.grand_total_usd as number) || 0;
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div className="border-b border-border px-6 py-5" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="MapIcon" size={24} className="text-primary" />
              My Trip Builder
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Build, save, compare, and book your custom travel packages
            </p>
          </div>
          <Link href="/dynamic-packages" className="btn-primary flex items-center gap-2">
            <Icon name="PlusIcon" size={16} />Build New Package
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Build Package', desc: 'Start from scratch', icon: 'PuzzlePieceIcon', href: '/dynamic-packages', color: 'text-primary' },
            { label: 'AI Optimize', desc: 'Let AI build for you', icon: 'SparklesIcon', href: '/dynamic-packages', color: 'text-amber-400' },
            { label: 'My Bookings', desc: 'View all bookings', icon: 'BookmarkIcon', href: '/my-bookings', color: 'text-green-400' },
            { label: 'AI Concierge', desc: 'Get travel advice', icon: 'ChatBubbleLeftRightIcon', href: '/ai-concierge', color: 'text-blue-400' },
          ].map((a) => (
            <Link key={a.label} href={a.href} className="card p-4 hover:border-primary/40 transition-colors">
              <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={22} className={`${a.color} mb-2`} />
              <div className="text-sm font-semibold text-foreground">{a.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{a.desc}</div>
            </Link>
          ))}
        </div>

        {/* Compare Banner */}
        {compareList.length > 0 && (
          <div className="card p-4 border-primary/30 bg-primary/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="ArrowsRightLeftIcon" size={18} className="text-primary" />
              <span className="text-sm font-medium text-foreground">{compareList.length} package{compareList.length > 1 ? 's' : ''} selected for comparison</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setActiveModal('compare'); }}
                disabled={compareList.length < 2}
                className="btn-primary text-xs px-4 py-1.5"
              >
                Compare Now
              </button>
              <button onClick={() => setCompareList([])} className="btn-secondary text-xs px-3 py-1.5">Clear</button>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {(['all', 'draft', 'confirmed', 'booked'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all border ${filter === f ? 'bg-primary/20 text-primary border-primary/50' : 'border-border text-muted-foreground hover:text-foreground'}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Packages Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card p-5 animate-pulse space-y-3">
                <div className="h-5 bg-surface-4 rounded w-3/4" />
                <div className="h-3 bg-surface-4 rounded w-1/2" />
                <div className="h-8 bg-surface-4 rounded" />
              </div>
            ))}
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="card p-12 text-center">
            <Icon name="MapIcon" size={40} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-foreground mb-2">No packages yet</h3>
            <p className="text-sm text-muted-foreground mb-6">Start building your first custom travel package with AI optimization</p>
            <Link href="/dynamic-packages" className="btn-primary inline-flex items-center gap-2">
              <Icon name="SparklesIcon" size={16} />Build Your First Package
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPackages.map((pkg) => {
              const total = getTotal(pkg);
              const components = pkg.components as Array<{ type: string; supplier: string; product_name: string; estimated_price_usd: number }>;
              const meta = pkg.metadata as Record<string, unknown>;
              const isComparing = compareList.includes(pkg.id);
              return (
                <div key={pkg.id} className={`card p-5 flex flex-col gap-3 transition-all ${isComparing ? 'border-primary/50 bg-primary/5' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon name={PACKAGE_TYPE_ICONS[pkg.package_type] as Parameters<typeof Icon>[0]['name'] || 'StarIcon'} size={16} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate">{pkg.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{pkg.package_type}</div>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${pkg.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : pkg.status === 'booked' ? 'bg-blue-500/20 text-blue-400' : 'bg-surface-4 text-muted-foreground'}`}>
                      {pkg.status}
                    </span>
                  </div>

                  {/* Component Icons */}
                  <div className="flex flex-wrap gap-1.5">
                    {components?.slice(0, 6).map((c, i) => (
                      <div key={i} title={c.type} className="w-6 h-6 rounded bg-surface-4 border border-border flex items-center justify-center">
                        <Icon name={COMPONENT_ICONS[c.type] as Parameters<typeof Icon>[0]['name'] || 'StarIcon'} size={12} className="text-muted-foreground" />
                      </div>
                    ))}
                    {components?.length > 6 && (
                      <div className="w-6 h-6 rounded bg-surface-4 border border-border flex items-center justify-center text-2xs text-muted-foreground">
                        +{components.length - 6}
                      </div>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {meta?.destination && <span className="flex items-center gap-1"><Icon name="MapPinIcon" size={11} />{meta.destination as string}</span>}
                    {meta?.duration && <span>{meta.duration as string}n</span>}
                    {meta?.travelers && <span className="flex items-center gap-1"><Icon name="UsersIcon" size={11} />{meta.travelers as string}</span>}
                  </div>

                  {/* Price */}
                  {total > 0 && (
                    <div className="text-lg font-bold text-primary">${total.toLocaleString()}</div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto pt-1">
                    <button
                      onClick={() => { setSelectedPackage(pkg); setActiveModal('detail'); }}
                      className="flex-1 btn-secondary text-xs py-1.5"
                    >
                      View
                    </button>
                    <button
                      onClick={() => toggleCompare(pkg.id)}
                      className={`btn-secondary text-xs py-1.5 px-2.5 ${isComparing ? 'border-primary/50 text-primary' : ''}`}
                      title="Compare"
                    >
                      <Icon name="ArrowsRightLeftIcon" size={13} />
                    </button>
                    <button
                      onClick={() => handleShare(pkg)}
                      className="btn-secondary text-xs py-1.5 px-2.5"
                      title="Share"
                    >
                      <Icon name="ShareIcon" size={13} />
                    </button>
                    {pkg.status === 'draft' && (
                      <Link href="/dynamic-packages" className="btn-primary text-xs py-1.5 px-3">
                        Book
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Package Detail Modal */}
      <LuxuryModal
        isOpen={activeModal === 'detail' && !!selectedPackage}
        onClose={() => setActiveModal(null)}
        title={selectedPackage?.name || 'Package Details'}
        size="lg"
      >
        {selectedPackage && (
          <div className="space-y-4 p-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Type', value: selectedPackage.package_type },
                { label: 'Status', value: selectedPackage.status },
                { label: 'Reference', value: selectedPackage.package_reference },
                { label: 'Created', value: new Date(selectedPackage.created_at).toLocaleDateString() },
              ].map((s) => (
                <div key={s.label} className="bg-surface-4 rounded-lg p-3 border border-border">
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                  <div className="text-sm font-semibold text-foreground mt-0.5 capitalize truncate">{s.value}</div>
                </div>
              ))}
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Components</h4>
              <div className="space-y-2">
                {(selectedPackage.components as Array<{ type: string; supplier: string; product_name: string; estimated_price_usd: number }>)?.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-surface-4 border border-border">
                    <div className="flex items-center gap-3">
                      <Icon name={COMPONENT_ICONS[c.type] as Parameters<typeof Icon>[0]['name'] || 'StarIcon'} size={16} className="text-primary" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{c.product_name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{c.type} · {c.supplier}</div>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-foreground">${c.estimated_price_usd?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Link href="/dynamic-packages" className="btn-primary flex items-center gap-2 flex-1 justify-center">
                <Icon name="PencilIcon" size={15} />Modify Package
              </Link>
              <button onClick={() => handleShare(selectedPackage)} className="btn-secondary flex items-center gap-2">
                <Icon name="ShareIcon" size={15} />Share
              </button>
            </div>
          </div>
        )}
      </LuxuryModal>

      {/* Compare Modal */}
      <LuxuryModal
        isOpen={activeModal === 'compare'}
        onClose={() => setActiveModal(null)}
        title="Compare Packages"
        size="lg"
      >
        <div className="overflow-x-auto p-1">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-xs text-muted-foreground pb-3 pr-4">Feature</th>
                {compareList.map((id) => {
                  const pkg = savedPackages.find((p) => p.id === id);
                  return (
                    <th key={id} className="text-left text-xs text-foreground pb-3 pr-4 font-semibold">
                      {pkg?.name || 'Package'}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {['Type', 'Status', 'Components', 'Total Price'].map((row) => (
                <tr key={row}>
                  <td className="py-2.5 pr-4 text-muted-foreground text-xs">{row}</td>
                  {compareList.map((id) => {
                    const pkg = savedPackages.find((p) => p.id === id);
                    if (!pkg) return <td key={id} className="py-2.5 pr-4">—</td>;
                    const val = row === 'Type' ? pkg.package_type
                      : row === 'Status' ? pkg.status
                      : row === 'Components' ? `${(pkg.components as unknown[])?.length || 0} items`
                      : `$${getTotal(pkg).toLocaleString()}`;
                    return <td key={id} className="py-2.5 pr-4 text-foreground capitalize">{val}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LuxuryModal>

      {/* Share Modal */}
      <LuxuryModal
        isOpen={activeModal === 'share'}
        onClose={() => setActiveModal(null)}
        title="Share Package"
      >
        <div className="space-y-4 p-1">
          <p className="text-sm text-muted-foreground">Share this package link with your travel companions or agent.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={shareLink}
              readOnly
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground"
            />
            <button onClick={handleCopyLink} className="btn-primary text-sm px-4">Copy</button>
          </div>
          <div className="flex gap-3">
            <button className="flex-1 btn-secondary flex items-center justify-center gap-2 text-sm">
              <Icon name="EnvelopeIcon" size={15} />Email
            </button>
            <button className="flex-1 btn-secondary flex items-center justify-center gap-2 text-sm">
              <Icon name="DocumentArrowDownIcon" size={15} />Download PDF
            </button>
          </div>
        </div>
      </LuxuryModal>
    </div>
  );
}
